/// <reference types="google.maps" />
/* global google:true */

/**
 * @file helper functions to interact with Google Map
 */

import { captureException } from '@@/bits/error_tracker'
import type { Cid, MapPostAttributes, PinDropSuggestion, PostAttributes } from '@@/types'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import { sortBy, uniqWith } from 'es-toolkit'
import { OverlappingMarkerSpiderfier } from 'ts-overlapping-marker-spiderfier'
const ZOOM_MAX = 21
const ZOOM_MIN = 2
/**
 * Abstractions for map
 * -- If we decide to switch out of Google Maps, the types and interfaces should be re-implement
 *
 * This module should only be used asynchronously after map api is loaded using map_api_loader.ts
 * Load this asynchronously by loading the vue component that is using this async or
 * e.g.
 * ```
 *  const { initMap, LatLng } = await import('@@/bits/map')
 * ```
 */

// TYPES AND INTERFACES
// interfaces shorthand
type LatLngLiteral = google.maps.LatLngLiteral
type BoundsLiteral = google.maps.LatLngBoundsLiteral
type MapOptions = google.maps.MapOptions
type MapsEventListener = google.maps.MapsEventListener
type Padding = google.maps.Padding
type MapTypeStyle = google.maps.MapTypeStyle
type MapTypeId = google.maps.MapTypeId
type OverlayView = google.maps.OverlayView
type PlacesService = google.maps.places.PlacesService
type Autocomplete = google.maps.places.Autocomplete
type AutocompletePrediction = google.maps.places.AutocompletePrediction
type AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken
type AutocompleteService = google.maps.places.AutocompleteService
type PlacesServiceStatus = google.maps.places.PlacesServiceStatus
type Geocoder = google.maps.Geocoder
type GeocoderResult = google.maps.GeocoderResult
type Point = google.maps.Point
type Icon = google.maps.Icon
type Size = google.maps.Size
type Polyline = google.maps.Polyline
type IconSequence = google.maps.IconSequence
type PlaceResult = google.maps.places.PlaceResult
type RankBy = google.maps.places.RankBy

// each map marker can be associated with a post
interface MarkerOptions extends google.maps.MarkerOptions {
  postId?: number
}
class Marker extends google.maps.Marker {
  constructor(opts?: MarkerOptions | null) {
    super(opts)
    if (opts?.postId) {
      this.postId = opts.postId
    }
  }
  postId?: number
}

interface MapDimensions {
  height: number
  width: number
}

// class types to be implemented
interface Bounds extends google.maps.LatLngBounds {
  extend: (point: LatLng | LatLngLiteral) => Bounds
}

interface GeoMap extends google.maps.Map {
  addListener: (eventName: string, handler: (...args: any[]) => void) => MapsEventListener
  setOptions: (options: MapOptions) => void
  fitBounds: (bounds: Bounds | BoundsLiteral, padding?: number | Padding) => void
  setCenter: (latlng: LatLng | LatLngLiteral) => void
  setZoom: (zoom: number) => void
}

interface LatLng extends google.maps.LatLng {
  /** Returns the latitude in degrees. */
  lat: () => number
  /** Returns the longitude in degrees. */
  lng: () => number
}

// Minimal marker clas/s
interface MarkerType {
  getPosition: () => LatLng
}

interface PostMarkerObject {
  id: Cid
  post: PostAttributes
  position: LatLngLiteral
  zIndex: number
}

interface PostConnectionObject {
  id: number
  from: MapPostAttributes
  to: MapPostAttributes
  label?: string
  labelCount?: number
  from_wish_id?: number
  to_wish_id?: number
}

// IMPLEMENTATION
const GoogleMaps = google.maps
const GeoMap = google.maps.Map
const OverlayView = google.maps.OverlayView
const Bounds = google.maps.LatLngBounds
const LatLng = google.maps.LatLng
const MapEventsManager = google.maps.event
const PlacesService = google.maps.places.PlacesService
const Geocoder = google.maps.Geocoder
const PlacesServiceStatus = google.maps.places.PlacesServiceStatus
const GeocoderStatus = google.maps.GeocoderStatus
const Point = google.maps.Point
const Size = google.maps.Size
const Autocomplete = google.maps.places.Autocomplete
const AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken
const AutocompleteService = google.maps.places.AutocompleteService
const ControlPosition = google.maps.ControlPosition
const Polyline = google.maps.Polyline
const RankBy = google.maps.places.RankBy

function getUniquePositionsFromPositions(positions: LatLngLiteral[]): LatLngLiteral[] {
  return uniqWith(positions, (p1, p2): boolean => p1.lat === p2.lat && p1.lng === p2.lng)
}
/**
 * Get bounds containing the list of positions
 * @param positions list of positions
 */
function getBoundsContainingPositions(positions: LatLngLiteral[]): Bounds {
  const bounds = new Bounds()
  const sortedPositions = positions.sort((p1, p2) => p2.lng - p1.lng)
  sortedPositions.forEach((position): void => {
    // Extending from west to east so that the bound will
    // fill up without crossing the International Date Line
    bounds.extend(position)
  })
  return bounds
}

function getBoundsZoomLevel(bounds: Bounds, mapDim: MapDimensions): number {
  const WORLD_DIM = { height: 256, width: 256 } // in pixels
  const ZOOM_VERTICAL_PADDING = 60 // in pixels - to leave enough space between the marker and the top/bottom edge for better viewability
  function latRad(lat: number): number {
    const sin = Math.sin((lat * Math.PI) / 180)
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
  }

  function calculateZoom(mapPx: number, worldPx: number, fraction: number): number {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2)
  }

  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()

  if (ne == null) {
    captureException(new Error('North East bound is null'), {
      context: {
        error: 'North East bound is null',
        boundsData: bounds ?? 'bounds is null or undefined',
        neData: ne ?? 'ne is null or undefined',
        swData: sw ?? 'sw is null or undefined',
      },
    })
    return 0
  }

  if (sw == null) {
    captureException(new Error('South West bound is null'), {
      context: {
        error: 'South West bound is null',
        boundsData: bounds ?? 'bounds is null or undefined',
        neData: ne ?? 'ne is null or undefined',
        swData: sw ?? 'sw is null or undefined',
      },
    })
    return 0
  }

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI

  const lngDiff = ne.lng() - sw.lng()
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360

  const latZoom = calculateZoom(mapDim.height - ZOOM_VERTICAL_PADDING * 2, WORLD_DIM.height, latFraction)
  const lngZoom = calculateZoom(mapDim.width, WORLD_DIM.width, lngFraction)
  return Math.min(latZoom, lngZoom, ZOOM_MAX)
}

function initMap(
  el: HTMLElement,
  latitude: number,
  longitude: number,
  defaultZoom: number,
  overridingOptions: google.maps.MapOptions = {},
): GeoMap {
  const map = new GeoMap(el, {
    clickableIcons: false,
    keyboardShortcuts: false,
    zoom: defaultZoom,
    center: { lat: latitude, lng: longitude },
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    maxZoom: ZOOM_MAX,
    // minZoom: ZOOM_MIN,
    zoomControl: false,
    // zoomControlOptions: {
    //   position: google.maps.ControlPosition.RIGHT_BOTTOM,
    // },
    tilt: 0,
    rotateControl: false,
    restriction: RESTRICTED_BOUND,
    ...overridingOptions,
  } as google.maps.MapOptions)
  const WATERCOLOR_MAX_ZOOM_SUPPORTED = 16
  map.mapTypes.set(
    'watercolor',
    new google.maps.ImageMapType({
      getTileUrl: (coord, zoom): string => {
        // "Wrap" x (longitude) at 180th meridian properly
        // NB: Don't touch coord.x: because coord param is by reference, and changing its x property breaks something in Google's lib
        const tilesPerGlobe = Math.pow(2, zoom)
        let x = coord.x % tilesPerGlobe
        if (x < 0) {
          x = tilesPerGlobe + x
        }
        // Handled by this Cloudflare snippet: https://dash.cloudflare.com/4b6faa068c6eb6ca0c73861f64020268/padlet.net/rules/snippets/edit/code-vscode/proxy_stadiamaps_watercolor
        // Cloudflare snippet is free for now, should be good enough until we move to new map with Leaflet
        return `https://padlet.net/tiles/stamen_watercolor/${zoom}/${x}/${coord.y}.jpg`
      },
      tileSize: new google.maps.Size(256, 256),
      name: 'Watercolor',
      maxZoom: WATERCOLOR_MAX_ZOOM_SUPPORTED,
    }),
  )
  return map
}

function getLocationNameSuggestionsForMap(
  location: LatLng,
  results: GeocoderResult[],
  map: GeoMap,
): PinDropSuggestion[] {
  const mapDiv = map.getDiv()
  const mapDim = {
    height: mapDiv.clientHeight,
    width: mapDiv.clientWidth,
  }
  const mapCurrentZoom = map.getZoom() ?? 0
  /**
   * Sorted map results
   * - Higher zoom level results are first:
   * If user is dropping a pin, user is selecting a location that is more precise than the current zoom
   *    - e.g. If the map is at city level, dropping a pin should be selecting a premise or a road or an area (depending on zoom)
   *    - e.g. If the map is at San Francisco Peninsula, dropping a pin should be selecting a city
   *    - e.g. If the map is at world map level, dropping a pin should be selecting a country
   */
  let endResults: PinDropSuggestion[] = results.map(
    (r: GeocoderResult): PinDropSuggestion => ({
      locationName: r.formatted_address,
      placeId: r.place_id,
      types: r.types.join(', '),
      zoom: r.geometry.bounds ? getBoundsZoomLevel(r.geometry.bounds, mapDim) : 22, // 21 is the max zoom. We use 22 for premises, which doesn't have bounds
    }),
  )
  // if current map view is too far, we filter out premises
  if (mapCurrentZoom < 14) {
    endResults = endResults.filter((r: PinDropSuggestion): boolean => r.zoom !== 22)
  }
  // if current map view is too close, we filter out postal codes
  if (mapCurrentZoom > 15) {
    endResults = endResults.filter((r: PinDropSuggestion): boolean => !r.types.includes('postal_code'))
  }
  endResults = sortBy(endResults, [
    (r: PinDropSuggestion) => {
      if (r.zoom > mapCurrentZoom + 1) {
        return r.zoom - mapCurrentZoom + 1
      }
      if (r.zoom > mapCurrentZoom) {
        return r.zoom - mapCurrentZoom + 50
      }
      return mapCurrentZoom - r.zoom + 100
    },
  ])
  endResults.push({
    locationName: location.toString(),
    placeId: location.toString(),
    zoom: 22,
  })
  return endResults
}

const RESTRICTED_BOUND: google.maps.MapRestriction = {
  latLngBounds: {
    north: 86,
    south: -85,
    west: -180,
    east: 180,
  },
  strictBounds: true,
}

const RELAXED_BOUND: google.maps.MapRestriction = {
  latLngBounds: {
    north: 90,
    south: -90,
    west: -180,
    east: 180,
  },
  strictBounds: false,
}

const NARNIA_LATITUDE = 42.5210781
const NARNIA_LONGITUDE = 12.5122799
const NARNIA_LATLNG = { lng: NARNIA_LONGITUDE, lat: NARNIA_LATITUDE }

// Polygon coordinators that is the marker shape
// to define the clickable region for the marker image
// https://gist.github.com/ncnlinh/aad007117c6ed32d5fa627727967acf0
const MARKER_SHAPE_POLY_COORDS: google.maps.MarkerShapePolyCoords = [
  18, 36, 17.999906539916992, 36.706695556640625, 21.000823974609375, 34.54119110107422, 25.36109733581543,
  36.77384948730469, 23.264013290405273, 40.50735855102539, 18.393840789794922, 41.49522399902344, 13.469114303588867,
  40.79499435424805, 10.300071716308594, 37.479957580566406, 14.232404708862305, 34.72127151489258, 24.228364944458008,
  27.995296478271484, 21.087316513061523, 31.884458541870117, 16.75306510925293, 33.29543685913086, 13.316431999206543,
  29.964298248291016, 10.308030128479004, 25.971935272216797, 7.656026840209961, 21.73595428466797, 5.637470245361328,
  17.170005798339844, 5.070603370666504, 12.242193222045898, 6.688174724578857, 7.553606033325195, 10.068451881408691,
  3.911986827850342, 14.539636611938477, 1.7345540523529053, 19.491361618041992, 1.3410000801086426, 24.229829788208008,
  2.8468921184539795, 28.124858856201172, 5.936671733856201, 30.529478073120117, 10.278027534484863, 30.845916748046875,
  15.223479270935059, 29.260351181030273, 19.949813842773438, 26.813871383666992, 24.306293487548828, 18.70505142211914,
  34.26530456542969,
]

export {
  Autocomplete,
  AutocompleteService,
  AutocompleteSessionToken,
  Bounds,
  ControlPosition,
  Geocoder,
  GeocoderStatus,
  GeoMap,
  getBoundsContainingPositions,
  getBoundsZoomLevel,
  getLocationNameSuggestionsForMap,
  getUniquePositionsFromPositions,
  GoogleMaps,
  initMap,
  LatLng,
  MapEventsManager,
  Marker,
  MARKER_SHAPE_POLY_COORDS,
  MarkerClusterer,
  NARNIA_LATITUDE,
  NARNIA_LATLNG,
  NARNIA_LONGITUDE,
  OverlappingMarkerSpiderfier,
  OverlayView,
  PlacesService,
  PlacesServiceStatus,
  Point,
  Polyline,
  RankBy,
  RELAXED_BOUND,
  RESTRICTED_BOUND,
  Size,
  ZOOM_MAX,
}
export type {
  AutocompletePrediction,
  BoundsLiteral,
  GeocoderResult,
  Icon,
  IconSequence,
  LatLngLiteral,
  MapDimensions,
  MapOptions,
  MapsEventListener,
  MapTypeId,
  MarkerOptions,
  MapTypeStyle,
  MarkerType,
  PlaceResult,
  PostConnectionObject,
  PostMarkerObject,
}
